import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ROLE_LABELS, formatDate, inputCls, labelCls, btnPrimary } from "@/lib/constants";
import { Modal } from "@/components/Modal";
import { toast } from "sonner";
import { Plus, Trash2, Shield, KeyRound, Pencil } from "lucide-react";

const empty = { name: "", email: "", password: "", role: "dsa", phone: "" };

export default function Users() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [pwdFor, setPwdFor] = useState(null);
  const [newPwd, setNewPwd] = useState("");

  const load = useCallback(() => api.get("/users").then((r) => setUsers(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/roles").then((r) => setRoles(r.data.filter((x) => x.key !== "partner"))).catch(() => {}); }, []);
  const roleLabel = (key) => roles.find((r) => r.key === key)?.label || ROLE_LABELS[key] || key;

  const savePwd = async () => {
    try {
      await api.post(`/users/${pwdFor.id}/set-password`, { password: newPwd });
      toast.success(`Password updated for ${pwdFor.name}`);
      setPwdFor(null); setNewPwd("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/users/${editId}`, { name: form.name, email: form.email, role: form.role, phone: form.phone });
      else await api.post("/users", form);
      toast.success(editId ? "Team member updated" : "Team member added");
      setShow(false); setForm(empty); setEditId(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const openNew = () => { setForm(empty); setEditId(null); setShow(true); };
  const openEdit = (u) => { setForm({ name: u.name, email: u.email, password: "", role: u.role, phone: u.phone || "" }); setEditId(u.id); setShow(true); };

  const del = async (id) => { if (!window.confirm("Remove this user?")) return; try { await api.delete(`/users/${id}`); toast.success("User removed"); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };

  const roleColor = { admin: "bg-slate-900 text-white", sales_manager: "bg-purple-100 text-purple-700", master_dsa: "bg-indigo-100 text-indigo-700", dsa: "bg-emerald-100 text-emerald-700", agent: "bg-blue-100 text-blue-700", partner: "bg-teal-100 text-teal-700" };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight">Team & Access</h1>
          <p className="text-sm text-slate-500 mt-1">Manage login accounts and roles</p>
        </div>
        <button data-testid="add-user-button" onClick={openNew} className={btnPrimary}><Plus size={16} /> Add Member</button>
      </div>

      <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead>
            <tr className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3">Role</th><th className="text-left px-4 py-3">Joined</th><th className="text-right px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-slate-100 hover:bg-slate-50/80 text-sm text-slate-700">
                <td className="px-4 py-3 font-medium text-slate-900">{u.name}</td>
                <td className="px-4 py-3">{u.email}</td>
                <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${roleColor[u.role] || "bg-slate-100 text-slate-700"}`}>{roleLabel(u.role)}</span></td>
                <td className="px-4 py-3 text-xs text-slate-400">{formatDate(u.created_at)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button data-testid={`set-password-${u.id}`} title="Set password" onClick={() => { setPwdFor(u); setNewPwd(""); }} className="p-2 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50"><KeyRound size={15} /></button>
                    <button data-testid={`edit-user-${u.id}`} title="Edit member" onClick={() => openEdit(u)} className="p-2 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50"><Pencil size={15} /></button>
                    {u.role !== "admin" && <button data-testid={`delete-user-${u.id}`} onClick={() => del(u.id)} className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 size={15} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={show} onClose={() => setShow(false)} title={editId ? "Edit Team Member" : "Add Team Member"} testId="user-modal">
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className={labelCls}>Name *</label><input data-testid="user-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Email *</label><input data-testid="user-email-input" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} /></div>
          {!editId && <div><label className={labelCls}>Password *</label><input data-testid="user-password-input" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} /></div>}
          <div><label className={labelCls}>Role *</label>
            <select data-testid="user-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls}>
              {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2"><label className={labelCls}>Phone</label><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} /></div>
          <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setShow(false)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium">Cancel</button>
            <button data-testid="submit-user-button" type="submit" className={btnPrimary}>{editId ? "Save Changes" : "Add Member"}</button>
          </div>
        </form>
      </Modal>

      <Modal open={!!pwdFor} onClose={() => setPwdFor(null)} title="Set Password" testId="set-password-modal">
        <div className="space-y-4">
          <div className="text-sm text-slate-600">Set a new login password for <span className="font-semibold text-slate-800">{pwdFor?.name}</span> ({pwdFor?.email}). This replaces their current password immediately.</div>
          <div>
            <label className={labelCls}>New Password *</label>
            <input data-testid="new-password-input" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className={inputCls} placeholder="Min 6 characters" />
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setPwdFor(null)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium">Cancel</button>
            <button data-testid="confirm-set-password-button" onClick={savePwd} disabled={newPwd.length < 6} className={`${btnPrimary} disabled:opacity-50`}>Update Password</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
